import { Capacitor } from '@capacitor/core';
import { yupResolver } from '@hookform/resolvers/yup';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Evacuations } from 'features/evacZones/components/Evacuations';
import {
  ClipboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MapProvider } from 'shared/map-exports';
import { useConfirm } from 'components/ConfirmDialog';
import { NotificationType } from 'features/notifications/notifications.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { getEvacZoneStylesFromGeoEvent } from 'shared/utils';
import { makeStyles } from 'tss-react/mui';
import { InternalNotesInput } from 'components/InternalNotesInput';
import {
  useEvacZonesChangedNotification,
  useRegionEvacZones,
} from 'features/evacZones/hooks/useRegionEvacZones';
import { MediaForm, getFileTypeFromFile } from 'components/MediaForm';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { enqueueSnackbar } from 'notistack';
import { useQueryParams } from 'hooks/useQueryParams';
import { WildfireManualDeactivationField } from 'features/wildfires/components/WildfireManualDeactivationField';
import { WildfireDeactivationAlert } from 'features/wildfires/components/WildfireDeactivationAlert';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { NotificationType as NotificationTypeConstant } from 'features/notifications/notifications.constants';
import { usePermissions } from 'hooks/usePermissions';
import { useAuthState } from 'state/useAuthState';
import { AcreageAndContainmentFields } from 'features/wildfires/components/AcreageAndContainmentFields';
import { PendingEvacZonesError } from 'features/evacZones/components/PendingEvacZonesError';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { AxiosError, HttpStatusCode, isAxiosError } from 'axios';
import { handlePendingReviewZonesErrorOrFallback } from 'features/geoEvents/utils/handlePendingReviewZonesErrorOrFallback';
import { ReportMessageField } from 'features/reports/components/ReportMessageField';
import { ReportNotificationPreview } from 'features/reports/components/ReportNotificationPreview';
import { ConfirmNotificationDialog } from 'features/reports/components/ConfirmNotificationDialog';
import { CriticalAlertConfirmation } from 'features/reports/components/CriticalAlertConfirmation';
import { InlineStatus } from 'components/InlineStatus';
import { REGION_EVAC_ZONES_REFETCH_INTERVAL } from 'features/evacZones/constants';
import {
  ImageUploadData,
  useImageUpload,
} from 'features/reports/hooks/useImageUpload';
import { EvacZone } from 'shared/types';
import { useFormBlocker } from 'hooks/useFormBlocker';
import {
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { FloodingDeactivationAlert } from 'features/flooding/components/FloodingDeactivationAlert';
import { FloodingManualDeactivationField } from 'features/flooding/components/FloodingManualDeactivationField';
import { PendingReviewEvacZoneErrorType } from 'features/geoEvents/geoEvent.types';
import { invalidateGeoEventQueries } from 'features/geoEvents/hooks/useGeoEvent';
import { useResetOnInitialValuesChange } from 'features/geoEvents/hooks/useResetOnInitialValuesChange';
import { getIncidentRemountKey } from 'features/geoEvents/utils/getIncidentRemountKey';
import { GeoEventFormTabs } from 'features/geoEvents/components/GeoEventFormTabs';
import { GeoEventRegionsSelector } from 'features/geoEvents/components/GeoEventRegionsSelector';
import { InternalFormSection } from 'features/geoEvents/components/InternalFormSection';
import { FloodingWarnings } from 'app/pages/CreateEditFlooding/FloodingWarnings';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { useProvisionalSubmissionMutations } from 'features/provisionalSubmissions/hooks/useProvisionalSubmissionMutations/useProvisionalSubmissionMutations';
import { CreateReportProvisionalSubmissionResponse } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import {
  getRedundancyMessage,
  getValuesForServer,
  getFormValidationSchema,
  getInitialFormValues,
  getReviewSubmissionInitialFormValues,
  invalidateReportsQueryCache,
  getDefaultNotificationType,
  isWildfireFormValues,
  getGeoEventReportFeatures,
  postReport,
  shouldShowAcreageWarning,
  SILENT_ACREAGE_WARNING_THRESHOLD,
} from './utils';
import {
  PostReportData,
  ReportFormQueryParams,
  ReportableGeoEvent,
  ReportFormValues,
  GeoEventFormStatus,
  GeoEventPatchConfigs,
} from './types';
import { ReportMap } from './ReportMap';
import { ReportGeoEventChangelog } from './ReportGeoEventChangelog';
import { ReviewSubmissionGeoEventChanges } from './ReviewSubmissionGeoEventChanges';
import { useFrozenWarningInputs } from './useFrozenWarningInputs';

const statusOptions: { label: string; value: GeoEventFormStatus }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const preserveFieldsOnReset: (keyof ReportFormValues)[] = [
  'messageHtml',
  'messageText',
  'media',
  'fileType',
  'embedUrl',
  'asset',
  'notificationType',
  'criticalAlertRegionIds',
  'lat',
  'lng',
  'az',
  'reporterOnlyNotes',
  'newReporterNote',
  'reporterNoteToggles',
];

export type ReportFormProps = {
  geoEvent: ReportableGeoEvent;
  geoEventPatchConfigs: GeoEventPatchConfigs;
  getViewPath: (geoEventId: number) => string;
  reviewSubmission?: CreateReportProvisionalSubmissionResponse;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  formContainer: {
    overflowY: 'auto',
    height: '100%',
    width: '100%',
    maxWidth: 820,
  },
  form: {
    // Top padding lives here, not on the scroll container, so it doesn't
    // offset sticky toolbars (e.g. the RichTextEditor's).
    paddingTop: theme.spacing(3),
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
  },
  notificationLabel: {
    textTransform: 'capitalize',
  },
  mapContainer: {
    width: '100%',
    height: 225,
    borderRadius: Number(theme.shape.borderRadius) * 1.34,
    overflow: 'hidden',
  },
}));

const getSubmitButtonKey = (
  isProvisional: boolean,
  isCriticalAlert: boolean,
): string => {
  if (isProvisional) return 'addIncidentReport.buttonProvisional';
  if (isCriticalAlert) return 'addIncidentReport.buttonCritical';
  return 'addIncidentReport.button';
};

export const ReportForm = (props: ReportFormProps): JSX.Element => {
  const { geoEvent, geoEventPatchConfigs, getViewPath, reviewSubmission } =
    props;
  const isReviewing = Boolean(reviewSubmission);
  const { classes } = useStyles();
  const { t } = useTranslation();
  const initialZoom = useInitialMapZoom();
  const {
    permissions: { canSendCriticalAlerts },
  } = useAuthState();

  const { data: permissions } = usePermissions({
    geoEventId: geoEvent.id,
    geoEventType: geoEvent.geoEventType,
  });
  const reportPermissions = permissions?.[geoEvent.geoEventType]?.report;
  const isProvisional = reportPermissions?.createPermission === 'provisional';
  const allowedAssetTypes = isProvisional
    ? reportPermissions?.allowedMediaAssetTypes
    : undefined;

  const [postError, setPostError] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('laptop'), {
    defaultMatches: !Capacitor.isNativePlatform(),
  });
  const queryClient = useQueryClient();
  const [queryParams] = useQueryParams<ReportFormQueryParams>();
  const [openConfirmNotifDialog, setOpenConfirmNotifDialog] = useState(false);
  const { hazards } = useAllGeoEvents();
  const [isCriticalConfirmed, setIsCriticalConfirmed] = useState(false);
  const confirm = useConfirm();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const {
    onZoneStateChanged,
    lockEvacuationZonesChangedNotification,
    unlockEvacuationZonesChangedNotification,
  } = useEvacZonesChangedNotification({ geoEventId: geoEvent.id });
  const evacZoneStyles =
    isWildfireGeoEvent(geoEvent) || isFloodingGeoEvent(geoEvent)
      ? getEvacZoneStylesFromGeoEvent(geoEvent)
      : [];

  const [validationRegionEvacZones, setValidationRegionEvacZones] = useState<
    EvacZone[]
  >([]);

  const validationSchema = getFormValidationSchema(
    t,
    geoEvent,
    validationRegionEvacZones,
  );
  const { supportsNotifications, supportsEvacZones, supportInternalNotes } =
    getGeoEventReportFeatures(geoEvent);
  const isInternalTabEnabled =
    useReleaseFlag('internal_tab_on_incidents') && supportInternalNotes;
  const initialValues = isReviewing
    ? getReviewSubmissionInitialFormValues(geoEvent, reviewSubmission!)
    : getInitialFormValues(geoEvent, queryParams);

  const frozenWarningInputs = useFrozenWarningInputs(
    geoEvent,
    isSubmittingReport,
  );

  const isWildfireEvent = isWildfireGeoEvent(geoEvent);
  const isFloodingEvent = isFloodingGeoEvent(geoEvent);

  const formMethods = useForm<ReportFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    control,
    formState,
    setValue,
    handleSubmit,
    watch,
    reset,
    getValues,
  } = formMethods;

  // Re-sync refreshed incident data into the form without clobbering the report content the user
  // is composing.
  useResetOnInitialValuesChange({
    values: initialValues,
    getValues,
    reset,
    preserveFields: preserveFieldsOnReset,
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const isPrescribed = isWildfireEvent
    ? (geoEvent as Wildfire).data.isPrescribed
    : false;
  const isComplexParent = isWildfireEvent
    ? (geoEvent as Wildfire).data.isComplexParent
    : false;

  const postReportMutation = useMutation({
    mutationFn: (update: {
      data: PostReportData;
      skipRedundancyCheck?: boolean;
    }) => postReport(update.data, update.skipRedundancyCheck),
  });

  const {
    submitProvisionalReport,
    approveProvisionalReport,
    rejectProvisionalSubmission,
    isRejecting,
  } = useProvisionalSubmissionMutations();

  const [asset, media, fileType, notificationType] = watch([
    'asset',
    'media',
    'fileType',
    'notificationType',
  ]);

  const isCriticalAlert = notificationType === NotificationType.Critical;
  const showCriticalAlertRegions =
    isCriticalAlert && geoEvent.regions.length > 1;

  const activeEvacuations = useWatch({
    control,
    name: 'activeEvacuations',
    defaultValue: initialValues.activeEvacuations,
    disabled: !supportsEvacZones,
  });

  // Only subscribe to wildfire-specific fields when needed to avoid unnecessary re-renders
  const manualDeactivationStarted = useWatch({
    control,
    name: 'manualDeactivationStarted',
    disabled: !isWildfireFormValues(initialValues),
  });
  const acreage = useWatch({
    control,
    name: 'acreage',
    disabled: !isWildfireFormValues(initialValues),
  });
  const containment = useWatch({
    control,
    name: 'containment',
    disabled: !isWildfireFormValues(initialValues),
  });
  const isFps = useWatch({
    control,
    name: 'isFps',
    disabled: !isWildfireFormValues(initialValues),
  });

  const imgData = useMemo((): ImageUploadData | null => {
    if (!media || !('type' in media) || !fileType) return null;
    return { file: media, fileType };
  }, [fileType, media]);

  const handleImageUploadError = useCallback(() => {
    enqueueSnackbar(t('addIncidentReport.imageUploadError'), {
      variant: 'error',
    });
  }, [t]);

  const {
    regionEvacZones,
    isLoading: isLoadingEvacZones,
    isError: isErrorEvacZones,
  } = useRegionEvacZones({
    regions: geoEvent.regions,
    evacZoneStatuses: geoEvent.evacZoneStatuses,
    refetchInterval: REGION_EVAC_ZONES_REFETCH_INTERVAL,
    onZoneStateChanged,
  });

  useEffect(() => {
    // Update the validation region evac zones when regions change
    setValidationRegionEvacZones(regionEvacZones);
  }, [regionEvacZones]);

  const {
    uploadImgPromise,
    uploadImageVerification,
    mediaUrl,
    error: uploadImgError,
  } = useImageUpload({
    imgData,
    onError: handleImageUploadError,
  });

  const goBack = (geoEventId: number): void => {
    unblockedNavigate(getViewPath(geoEventId));
  };

  const handleBadRequestError = async (
    error: AxiosError,
    values: ReportFormValues,
  ): Promise<void> => {
    await handlePendingReviewZonesErrorOrFallback({
      error,
      queryClient,
      t,
      currentZonesWithStatus: values.evacZoneStatuses,
      updateFormZones: (zonesWithStatus) =>
        setValue('evacZoneStatuses', zonesWithStatus),
      geoEventId: geoEvent.id,
      fallbackAction: () =>
        logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error }),
    });
  };

  const handleReportMutationError = async (
    error: unknown,
    values: ReportFormValues,
  ): Promise<void> => {
    if (isAxiosError(error)) {
      if (error.response?.status === HttpStatusCode.Forbidden) {
        setPostError(true);
        return;
      }
      if (error.response?.status === HttpStatusCode.BadRequest) {
        await handleBadRequestError(error, values);
        return;
      }
    }
    logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
  };

  const handleReject = async (): Promise<void> => {
    if (!reviewSubmission) return;
    const confirmed = await confirm(
      t('reviewSubmission.rejectConfirm.message'),
      {
        title: t('reviewSubmission.rejectConfirm.title'),
      },
    );
    if (!confirmed) return;
    try {
      await rejectProvisionalSubmission(reviewSubmission.id);
    } catch (error: unknown) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      return;
    }
    enqueueSnackbar(t('reviewSubmission.snackbar.rejected'), {
      variant: 'success',
    });
    unblockedNavigate('/');
  };

  const handleSubmitReport: SubmitHandler<ReportFormValues> = async (
    values,
  ) => {
    if (
      !openConfirmNotifDialog &&
      values.notificationType === NotificationType.Normal &&
      getDefaultNotificationType(geoEvent) === NotificationType.Silent
    ) {
      setOpenConfirmNotifDialog(true);
      return;
    }

    if (openConfirmNotifDialog) setOpenConfirmNotifDialog(false);

    if (reviewSubmission) {
      const reportData = getValuesForServer({
        currentGeoEvent: geoEvent,
        updates: values,
        mediaUrl,
        geoEventPatchConfigs,
      });

      // If we have provisional submission media and the reviewer neither added nor deleted
      // their own media, then we send the provisional submission media
      const provisionalSubmissionMedia = reviewSubmission.media[0];
      const reviewerUploadedMedia = values.media !== null;
      const reviewerDeletedMedia = values.asset !== 'image';

      if (
        !reviewerUploadedMedia &&
        !reviewerDeletedMedia &&
        provisionalSubmissionMedia != null
      ) {
        reportData.media = [{ id: provisionalSubmissionMedia.id }];
      }

      try {
        const result = await approveProvisionalReport(
          reviewSubmission.id,
          reportData,
        );
        if (!result) return;
      } catch (error: unknown) {
        await handleReportMutationError(error, values);
        return;
      }
      enqueueSnackbar(t('reviewSubmission.snackbar.approved'), {
        variant: 'success',
      });
      goBack(reviewSubmission.submittedData.geoEventId);
      return;
    }

    setIsSubmittingReport(true);
    const reportData = getValuesForServer({
      currentGeoEvent: geoEvent,
      updates: values,
      mediaUrl,
      geoEventPatchConfigs,
    });

    try {
      if (uploadImgPromise) {
        const uploadResult = await uploadImgPromise;
        if (uploadResult instanceof Error) {
          return;
        }

        //  Verify that the upload actually happened
        if (uploadImageVerification) {
          const imageIsUploaded = await uploadImageVerification();
          if (!imageIsUploaded) {
            handleImageUploadError();
            return;
          }
        }
      }

      lockEvacuationZonesChangedNotification();

      let successMessage = t('addIncidentReport.successMessage');
      if (isProvisional) {
        const result = await submitProvisionalReport(reportData);
        if (!result) return;
        successMessage = t('addIncidentReport.provisionalSuccessMessage');
      } else {
        await postReportMutation.mutateAsync({ data: reportData });

        await Promise.all([
          invalidateReportsQueryCache(queryClient, geoEvent.id),
          invalidateGeoEventQueries(queryClient, geoEvent.id),
        ]);
      }

      enqueueSnackbar(successMessage, {
        variant: 'success',
      });
      goBack(geoEvent.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      unlockEvacuationZonesChangedNotification();
      const errorStatus = error?.response?.status;

      if (errorStatus === HttpStatusCode.Forbidden) {
        setPostError(true);
        return;
      }

      if (errorStatus === HttpStatusCode.Conflict) {
        // We should check for redundant posts (this only affects CIOs)
        // If there are too many posts in the last few minutes, we should warn them
        const resp = await confirm(getRedundancyMessage(error.response));
        if (!resp) return;

        try {
          await postReportMutation.mutateAsync({
            data: reportData,
            skipRedundancyCheck: true,
          });

          await Promise.all([
            invalidateReportsQueryCache(queryClient, geoEvent.id),
            invalidateGeoEventQueries(queryClient, geoEvent.id),
          ]);

          enqueueSnackbar(t('addIncidentReport.successMessage'), {
            variant: 'success',
          });
          goBack(geoEvent.id);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (finalError: any) {
          const finalErrorStatus = finalError?.response?.status;

          if (finalErrorStatus === HttpStatusCode.BadRequest) {
            await handleBadRequestError(finalError, values);
            return;
          }

          logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error: finalError });
        }

        return;
      }

      if (errorStatus === HttpStatusCode.BadRequest) {
        await handleBadRequestError(error, values);
        return;
      }

      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const handlePaste: ClipboardEventHandler<HTMLFormElement> = (event) => {
    /**
     * This handles copy-paste on the web.  In order to use,
     * the user must first click on the page to paste
     */
    if (Capacitor.getPlatform() !== 'web' || asset !== 'image') return;

    const clipboardItems = event.clipboardData.items;

    const items: DataTransferItem[] = [].slice
      .call(clipboardItems)
      .filter((item: DataTransferItem) => item.type.indexOf('image') !== -1);

    if (items.length === 0) {
      return;
    }

    const item = items[0];
    // Get the blob of image
    const blob = item.getAsFile();
    if (!blob) return;

    setValue('media', blob, { shouldDirty: true });
    setValue('fileType', getFileTypeFromFile(blob), { shouldDirty: true });
  };

  const isSubmittingOrRejecting = formState.isSubmitting || isRejecting;

  const submitButtons = reviewSubmission ? (
    <Grid size={12} container spacing={2}>
      <Grid size={6}>
        <Button
          type="submit"
          fullWidth
          size="large"
          color="success"
          variant="contained"
          disabled={isSubmittingOrRejecting}
        >
          {formState.isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('reviewSubmission.buttons.approve')
          )}
        </Button>
      </Grid>
      <Grid size={6}>
        <Button
          fullWidth
          size="large"
          color="error"
          variant="contained"
          onClick={handleReject}
          disabled={isSubmittingOrRejecting}
        >
          {isRejecting ? (
            <CircularProgress size={24} />
          ) : (
            t('reviewSubmission.buttons.reject')
          )}
        </Button>
      </Grid>
    </Grid>
  ) : (
    <Grid size={12}>
      <Button
        type="submit"
        fullWidth
        size="large"
        disabled={
          formState.isSubmitting ||
          uploadImgError ||
          (isCriticalAlert && !isCriticalConfirmed)
        }
      >
        {formState.isSubmitting ? (
          <CircularProgress size={24} />
        ) : (
          t(getSubmitButtonKey(isProvisional, isCriticalAlert))
        )}
      </Button>
    </Grid>
  );

  const formFields = (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      {postError && (
        <Grid size={12}>
          <Alert severity="error">
            <AlertTitle>{t('addIncidentReport.postError.title')}</AlertTitle>
            <Typography>
              {t('addIncidentReport.postError.description')}
            </Typography>
          </Alert>
        </Grid>
      )}

      {isProvisional && (
        <Grid size={12}>
          <Alert severity="info">
            {t('addIncidentReport.provisionalBanner')}
          </Alert>
        </Grid>
      )}

      {reviewSubmission && (
        <Grid size={12}>
          <ReviewSubmissionGeoEventChanges
            geoEvent={geoEvent}
            reviewSubmission={reviewSubmission}
            evacZones={regionEvacZones}
            zoneStyle={evacZoneStyles[0]}
          />
        </Grid>
      )}

      {!isInternalTabEnabled && (
        <Grid size={12}>
          <Typography variant="h3">
            {t(
              isHazardGeoEvent(geoEvent)
                ? 'addIncidentReport.sections.hazard'
                : 'addIncidentReport.sections.incident',
            )}
          </Typography>
        </Grid>
      )}

      <Grid size={12}>
        <Controller
          name="geoEventId"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-incident"
                label={t(
                  isHazardGeoEvent(geoEvent)
                    ? 'addIncidentReport.inputs.hazard.label'
                    : 'addIncidentReport.inputs.incident.label',
                )}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                className={classes.notificationLabel}
                disabled
              >
                <MenuItem
                  value={geoEvent.id}
                  className={classes.notificationLabel}
                >
                  {geoEvent.name}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-status"
                label={t('addIncidentReport.inputs.status.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                className={classes.notificationLabel}
                disabled={formState.isSubmitting}
              >
                {statusOptions.map(({ label, value }) => {
                  return (
                    <MenuItem
                      key={label}
                      value={value}
                      className={classes.notificationLabel}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            );
          }}
        />
      </Grid>

      {isFloodingEvent && (
        <>
          <Grid size={12}>
            <FloodingDeactivationAlert
              hasEvacZones={Boolean(regionEvacZones.length)}
            />
          </Grid>

          <Grid size={12}>
            <FloodingManualDeactivationField
              hasEvacZones={Boolean(regionEvacZones.length)}
            />
          </Grid>
        </>
      )}

      {isWildfireEvent && (
        <>
          <Grid size={12}>
            <AcreageAndContainmentFields isPrescribed={isPrescribed} />
          </Grid>

          <Grid size={12}>
            <WildfireDeactivationAlert
              hasEvacZones={Boolean(regionEvacZones.length)}
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Controller
                name="isFps"
                control={control}
                render={({ field }): JSX.Element => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isFps"
                          checked={field.value as boolean}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label={t('addIncidentReport.forwardProgressStopped')}
                      disabled={
                        formState.isSubmitting ||
                        isPrescribed ||
                        isComplexParent ||
                        (Boolean(manualDeactivationStarted) &&
                          !(field.value as boolean))
                      }
                      sx={{ width: 'fit-content' }}
                    />
                  );
                }}
              />

              <WildfireManualDeactivationField
                hasEvacZones={Boolean(regionEvacZones.length)}
              />
            </Box>
          </Grid>
        </>
      )}

      {!isPrescribed && supportsEvacZones && (
        <Grid size={12}>
          <Controller
            name="activeEvacuations"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={
                    activeEvacuations ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <ChevronRightIcon />
                    )
                  }
                  onClick={() => field.onChange(!field.value)}
                  disabled={formState.isSubmitting}
                >
                  <Typography variant="h3">
                    {t('addIncidentReport.activeEvacuations')}
                  </Typography>
                </Button>
              );
            }}
          />
        </Grid>
      )}

      {supportsEvacZones && (
        <Grid size={12}>
          {/* Ideally we would use MUI's <Collapse /> here, but there
                    are some performance problems with iOS Safari:
                    https://linear.app/watchduty/issue/ENG-1377/slowdown-on-certain-add-report-pages */}
          <Box
            sx={{
              display: activeEvacuations ? 'block' : 'none',
            }}
          >
            {isLoadingEvacZones || isErrorEvacZones ? (
              <InlineStatus status={isLoadingEvacZones ? 'loading' : 'error'} />
            ) : (
              <Evacuations
                key={getIncidentRemountKey('evacuations', geoEvent)}
                evacZones={regionEvacZones}
                zoneStyles={evacZoneStyles}
                shouldRenderTable={activeEvacuations}
                disabled={isProvisional}
              />
            )}
          </Box>
        </Grid>
      )}

      {supportInternalNotes && !isInternalTabEnabled && (
        <>
          <Grid size={12}>
            <Box sx={{ marginTop: 1 }}>
              <Typography variant="h3">
                {t('createEditWildfire.sections.internal')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <InternalNotesInput
              initialValue={initialValues.reporterOnlyNotes}
            />
          </Grid>
        </>
      )}

      <Grid size={12}>
        <Typography variant="h3">
          {t('addIncidentReport.sections.update')}
        </Typography>
      </Grid>

      {supportsNotifications && (
        <Grid size={12}>
          <Controller
            name="notificationType"
            control={control}
            render={({ field, fieldState }): JSX.Element => {
              const { ref, ...muiFieldProps } = field;
              return (
                <TextField
                  select
                  id="field-control-notificationType"
                  label={t('addIncidentReport.inputs.notificationType.label')}
                  fullWidth
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  required
                  className={classes.notificationLabel}
                  disabled={
                    geoEvent.notificationType === NotificationType.Silent ||
                    formState.isSubmitting
                  }
                >
                  {[
                    NotificationTypeConstant.NORMAL,
                    NotificationTypeConstant.SILENT,
                    NotificationTypeConstant.CRITICAL,
                  ].map((notifType) => {
                    return (
                      <MenuItem
                        key={notifType}
                        value={notifType}
                        className={classes.notificationLabel}
                        disabled={
                          notifType === NotificationTypeConstant.CRITICAL &&
                          !canSendCriticalAlerts
                        }
                      >
                        {t(`common.${notifType}`)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              );
            }}
          />
        </Grid>
      )}
      {showCriticalAlertRegions && (
        <Grid size={12} sx={{ pb: 2 }}>
          <Controller
            name="criticalAlertRegionIds"
            control={control}
            render={({ field, fieldState }): JSX.Element => (
              <GeoEventRegionsSelector
                regions={geoEvent.regions}
                selectedRegionIds={field.value}
                onChange={field.onChange}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error
                    ? fieldState.error?.message
                    : t('editGeoEvent.inputs.criticalAlertCounties.hint')
                }
                disabled={formState.isSubmitting}
              />
            )}
          />
        </Grid>
      )}
      <Grid size={12}>
        <ReportMessageField
          name="messageHtml"
          initialValue={initialValues.messageHtml}
          initialValues={{
            acreage: frozenWarningInputs.initialAcreage,
            containment: frozenWarningInputs.initialContainment,
          }}
          currentValues={{
            acreage,
            containment,
            isFps,
            notificationType,
          }}
          plainTextFieldName="messageText"
          isCritical={isCriticalAlert}
        />
      </Grid>

      {isWildfireEvent &&
        // Hide the warning for Silent incidents: the notification
        // field is disabled, so switching to Normal isn't possible.
        geoEvent.notificationType !== NotificationType.Silent &&
        shouldShowAcreageWarning({
          initialAcreage: frozenWarningInputs.initialAcreage,
          acreage,
          notificationType,
          containment: frozenWarningInputs.initialContainment,
          isFps: frozenWarningInputs.initialIsFps,
          manualDeactivationStarted:
            frozenWarningInputs.initialManualDeactivationStarted,
        }) && (
          <Grid size={12}>
            <Alert severity="error">
              {t('addIncidentReport.acreageSilentUpdateWarning', {
                percent: Math.round(SILENT_ACREAGE_WARNING_THRESHOLD * 100),
              })}
            </Alert>
          </Grid>
        )}

      <MediaForm
        geoEvent={geoEvent}
        latFieldName="lat"
        lngFieldName="lng"
        azFieldName="az"
        allowedAssetTypes={allowedAssetTypes}
        mediaAttribution={reviewSubmission?.media?.[0]?.attribution ?? null}
        isReviewing={isReviewing}
      />

      <Grid size={12}>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h3">
            {t('addIncidentReport.sections.preview')}
          </Typography>
        </Box>
      </Grid>

      <ReportNotificationPreview
        geoEvent={geoEvent}
        supportsNotifications={supportsNotifications}
      />

      <Grid size={12}>
        <ReportGeoEventChangelog
          geoEvent={geoEvent}
          evacZones={regionEvacZones}
          zoneStyle={evacZoneStyles[0]}
        />
      </Grid>

      {isFloodingEvent && (
        <Grid size={12}>
          <FloodingWarnings flooding={geoEvent} hazards={hazards} />
        </Grid>
      )}

      {supportsEvacZones && formState.errors.evacZoneStatuses?.message && (
        <Grid size={12}>
          <PendingEvacZonesError
            zoneNames={formState.errors.evacZoneStatuses.message}
            type={
              formState.errors.evacZoneStatuses
                .type as PendingReviewEvacZoneErrorType
            }
          />
        </Grid>
      )}

      {isCriticalAlert && (
        <Grid size={12}>
          <CriticalAlertConfirmation
            onConfirmationChange={setIsCriticalConfirmed}
          />
        </Grid>
      )}

      {submitButtons}
    </Grid>
  );

  return (
    <MapProvider>
      <FormProvider {...formMethods}>
        <Box
          className={classes.root}
          sx={{
            flexDirection: isLargeMediaQuery ? 'row' : 'column',
            overflow: isLargeMediaQuery ? 'hidden' : 'auto',
          }}
          ref={scrollContainerRef}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: isLargeMediaQuery ? '100%' : 314,
              display: 'flex',
            }}
          >
            <ReportMap
              geoEvent={geoEvent}
              disableStructuredEvacZoneLayerClick={isProvisional}
              zoom={isReviewing ? undefined : initialZoom}
            />
          </Box>
          <Container
            maxWidth={false}
            disableGutters={isInternalTabEnabled}
            className={isLargeMediaQuery ? classes.formContainer : undefined}
          >
            <FormWithoutEnter
              onSubmit={handleSubmit(handleSubmitReport)}
              noValidate
              className={isInternalTabEnabled ? undefined : classes.form}
              onPaste={handlePaste}
            >
              {isInternalTabEnabled ? (
                <GeoEventFormTabs<ReportFormValues>
                  idPrefix="report-form"
                  internalFieldNames={['newReporterNote']}
                  internalContent={
                    <InternalFormSection
                      geoEventId={geoEvent.id}
                      newReporterNoteInitialValue={
                        initialValues.newReporterNote
                      }
                    />
                  }
                >
                  {formFields}
                </GeoEventFormTabs>
              ) : (
                formFields
              )}

              <ConfirmNotificationDialog
                open={openConfirmNotifDialog}
                onClose={() => setOpenConfirmNotifDialog(false)}
                onConfirm={() => handleSubmit(handleSubmitReport)()}
              />
            </FormWithoutEnter>
          </Container>
        </Box>
      </FormProvider>
    </MapProvider>
  );
};
